import { ClaudeButton } from './ClaudeButton';
import { useEffect, useState } from 'react';
import { DeliverySync } from './DeliverySync';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { LocaleToggle, useT } from '../i18n';
import { SitePicker } from '../sites';
import { DecoRule, Diamond, OakMark, Wordmark } from './brand';
import { OfflineBanner } from './ui';
import { useOrderDayNav } from '../pages/OrderDaySection';
import { kindAt } from '../pages/VendorDocsPage';

/** The phone header: the oak, the wordmark, the language toggle, the rule. Identical on every screen. */
export function TopBar() {
  const t = useT();
  return (
    <header className="topbar">
      <div className="topbar-row">
        <Link to="/" className="brand" aria-label={t('appName')}>
          <OakMark />
          <Wordmark />
        </Link>
        <SitePicker />
        <LocaleToggle />
      </div>
      <DecoRule />
    </header>
  );
}

function useSections() {
  const t = useT();
  return [
    { to: '/', label: t('home'), end: true },
    { to: '/orders', label: t('orders'), end: false },
    { to: '/items', label: t('items'), end: false },
    { to: '/stock', label: t('stock'), end: false },
    { to: '/customers', label: t('customers'), end: false },
  ];
}

/** `active` overrides the path match when an address belongs to an entry by more than its path. */
type NavItem = { to: string; label: string; end?: boolean; active?: (pathname: string, search: string) => boolean };
/** `key` names the group for its open/closed state (labels change with the language). */
type NavGroup = { key: string; label: string | null; items: NavItem[]; owns?: string[] };

/** The office's full navigation, grouped the way the business works; each item shows only to the roles that may open it. */
function useNavGroups(): NavGroup[] {
  const t = useT();
  const { me } = useAuth();
  const role = me?.role ?? 'readonly';
  const office = role === 'owner' || role === 'office';
  const sales = office || role === 'sales';
  const crew = office || role === 'warehouse' || role === 'driver' || role === 'sales';
  const orderDay = useOrderDayNav(office);
  const groups: NavGroup[] = [
    { key: 'home', label: null, items: [{ to: '/', label: t('navDashboard'), end: true }] },
    {
      key: 'sell',
      label: t('navSell'),
      owns: ['/invoices', '/orders', '/paste', '/prices', '/items', '/lost-sales'],
      items: [
        ...(office ? [{ to: '/invoices/new', label: t('newInvoice') }, { to: '/paste', label: t('pasteTitle') }, { to: '/invoices', label: t('invoicesTitle'), end: true }] : []),
        // every role finds the orders (decision 184); the owner and the office invoice straight, and reach an order from
        // its invoice (JL, 2026-11-03: "remove it ... and keep it")
        ...(office ? [] : [{ to: '/orders', label: t('orders') }]),
        // the price lists sent to customers, a click away (decision 172); Price management lights only on its own page
        ...(office ? [{ to: '/prices', label: t('pricesTitle'), end: true }, { to: '/prices/list', label: t('navPriceLists') }] : []),
        { to: '/items', label: t('navProducts') },
        // the vendors' codes and the products they are linked to (decision 150)
        ...(office ? [{ to: '/items/codes', label: t('navVendorCodes') }] : []),
        // everything not linked, in one Excel file filled and uploaded (decision 157)
        ...(office ? [{ to: '/items/linking', label: t('linkingTitle') }] : []),
        // whoever hears that a sale did not happen records it (decision 97)
        ...(crew || role === 'readonly' ? [{ to: '/lost-sales', label: t('lostSalesTitle') }] : []),
      ],
    },
    // the loads the business buys for (Tuesday, Friday), as JL's Order Guide has them (decision 76)
    ...(office ? [{ key: 'orderDay', label: t('navOrderDay'), items: orderDay, owns: ['/order-day'] }] : []),
    {
      key: 'customers',
      label: t('navCustomers'),
      owns: ['/customers', '/forecast', '/texts'],
      items: [
        { to: '/customers', label: t('customers') },
        // the CRM is a tab of Customers now (decision 169)
        ...(sales ? [{ to: '/forecast', label: t('forecastTitle') }] : []),
        ...(sales ? [{ to: '/texts', label: t('textsTitle') }] : []),
      ],
    },
    ...(office
      ? [
          {
            key: 'buy',
            label: t('navBuyStock'),
            owns: ['/costs', '/vendor-prices', '/vendors'],
            items: [
              { to: '/vendors', label: t('vendorsTitle') },
              { to: '/costs/bills', label: t('vendorBills'), active: (p: string, q: string) => kindAt(p, q) === 'bills' },
              { to: '/costs/price-lists', label: t('vendorPriceLists'), active: (p: string, q: string) => kindAt(p, q) === 'lists' },
              { to: '/vendor-prices', label: t('vendorPricesTitle') },
              { to: '/costs/papers', label: t('papersTitle') },
            ],
          },
        ]
      : []),
    {
      key: 'warehouse',
      label: t('navWarehouse'),
      owns: ['/stock', '/route', '/compliance'],
      items: [
        // the count owns the stock screens, except the prep card, which has its own entry
        { to: '/stock', label: t('countTitle'), active: (p: string) => (p === '/stock' || p.startsWith('/stock/')) && !p.startsWith('/stock/prep') },
        ...(role && ['owner', 'office', 'warehouse'].includes(role) ? [{ to: '/stock/prep', label: t('prepTitle') }] : []),
        ...(crew ? [{ to: '/route', label: t('routesTitle') }] : []),
        ...(role && ['owner', 'office', 'warehouse', 'driver'].includes(role) ? [{ to: '/compliance', label: t('complianceTitle') }] : []),
      ],
    },
    ...(office
      ? [
          {
            key: 'finance',
            label: t('navFinance'),
            owns: ['/money', '/work', '/qbo', '/reports/aging', '/reports/statements'],
            items: [
              { to: '/money/receive', label: t('receivePaymentTitle') },
              { to: '/work', label: t('worklistsTitle') },
              { to: '/money/day', label: t('endOfDayTitle') },
              { to: '/money/month-end', label: t('monthEndTitle') },
              { to: '/reports/aging', label: t('navReceivable') },
              { to: '/settings/ledger', label: t('ledgerTitle') },
              { to: '/money/payables', label: t('payablesTitle') },
              { to: '/qbo/matching', label: t('qboMatchTitle') },
            ],
          },
          {
            key: 'reports',
            label: t('navReports'),
            owns: ['/reports/sales', '/reports/workload', '/reports/profit', '/reports/pnl', '/reports/costs-prices', '/reports/stale-prices', '/reports/price-changes', '/reports/exceptions'],
            items: [
              { to: '/reports/sales', label: t('salesTitle') },
              { to: '/reports/workload', label: t('workloadTitle') },
              { to: '/reports/profit', label: t('profitTitle') },
              // QuickBooks' own Profit and Loss, read live (decision 198)
              { to: '/reports/pnl', label: t('pnlTitle') },
              // Mata's against USDA, our costs and prices, the KPIs (decision 163)
              { to: '/reports/costs-prices', label: t('costsPricesTitle') },
              { to: '/reports/stale-prices', label: t('stalePricesTitle') },
              // every tier price that moved, who or auto (decision 161)
              { to: '/reports/price-changes', label: t('priceChangesTitle') },
              { to: '/reports/exceptions', label: t('exceptionsTitle') },
            ],
          },
        ]
      : []),
    {
      key: 'settings',
      label: t('navSettings'),
      owns: ['/settings'],
      items: [
        ...(role === 'owner' ? [{ to: '/settings/users', label: t('usersTitle') }, { to: '/settings/activity', label: t('activityTitle') }, { to: '/settings/sites', label: t('sitesTitle') }, { to: '/settings/connector', label: t('connectorTitle') }, { to: '/settings/quickbooks', label: t('qboTitle') }, { to: '/settings/email', label: t('emailSettingsTitle') }, { to: '/settings/payments', label: t('paymentsTitle') }, { to: '/settings/paca', label: t('pacaTitle') }, { to: '/settings/portal', label: t('portalSettingsTitle') }, { to: '/settings/system', label: t('systemTitle') }] : []),
        // the register's categories: the owner chooses, the office can look (decision 159)
        ...(office ? [{ to: '/settings/clover', label: t('cloverTitle') }] : []),
        { to: '/settings/password', label: t('changePassword') },
      ],
    },
  ];
  return groups.filter((g) => g.items.length > 0);
}

/** What a person keeps on this device (decision 172): the pages pinned to the top and the groups left open, each a
 *  list of strings. Browser storage, per person: a blocked, empty or garbled store means nothing pinned and the usual
 *  groups; another tab's change is followed; a write starts from what is stored now, so two tabs never undo each other. */
function useKeptList(name: string, userId: string | undefined): [string[], (change: (cur: string[]) => string[]) => void] {
  const key = `morales.nav.${name}.${userId ?? 'anon'}`;
  const read = (): string[] => {
    try {
      const v: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
      return Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string'))] : [];
    } catch {
      return [];
    }
  };
  const [value, setValue] = useState<string[]>(read);
  useEffect(() => {
    setValue(read());
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) setValue(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);
  const update = (change: (cur: string[]) => string[]) => {
    const next = [...new Set(change(read()))];
    setValue(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // kept for this visit only
    }
  };
  return [value, update];
}

/** An address as compared: decoded (a load named Miércoles) and in lower case, as the router matches it. */
const norm = (p: string): string => {
  try {
    return decodeURIComponent(p).toLowerCase();
  } catch {
    return p.toLowerCase();
  }
};
const under = (path: string, to: string) => path === to || path.startsWith(`${to}/`);

/**
 * One app, two layouts, decided by CSS (docs/DESIGN.md): under 900 px the phone app (header + bottom
 * tabs), from 900 px the office layout with the navy sidebar. Both navs are rendered; the stylesheet
 * shows one.
 */
export function Layout() {
  const t = useT();
  const { pathname, search } = useLocation();
  const sections = useSections();
  const groups = useNavGroups();
  const { me, signOut } = useAuth();
  // JL: "the dashboard menu on the left is getting way too extensive so maybe we can nest it. and have the option to
  // bookmark certain pages so that those are bookmarked and organized at the top" (decision 172)
  const [pins, setPins] = useKeptList('pins', me?.id);
  const [openKeys, setOpenKeys] = useKeptList('open', me?.id);
  const path = norm(pathname);
  // one test for the highlight and for which group is open, so the two never disagree
  const isActive = (s: NavItem) => (s.active ? s.active(pathname, search) : s.end ? path === norm(s.to) : under(path, norm(s.to)));
  const items = groups.flatMap((g) => g.items);
  const linkHere = items.some(isActive);
  // only pages this person may open (a role changed since stays unpinned on screen)
  const pinned = pins.flatMap((to) => items.find((i) => i.to === to) ?? []);
  const togglePin = (to: string) => setPins((cur) => (cur.includes(to) ? cur.filter((p) => p !== to) : [...cur, to]));
  const toggleGroup = (key: string) => setOpenKeys((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  const link = (s: NavItem, where: string) => (
    <div key={`${where}${s.to}`} className={pins.includes(s.to) ? 'sidelink-row pinned' : 'sidelink-row'}>
      <NavLink to={s.to} end={s.end ?? false} className={isActive(s) ? 'sidelink active' : 'sidelink'}>
        <Diamond filled={isActive(s)} />
        <span>{s.label}</span>
      </NavLink>
      <button type="button" className="pin-btn" aria-pressed={pins.includes(s.to)} aria-label={`${pins.includes(s.to) ? t('navUnpin') : t('navPin')}: ${s.label}`} title={pins.includes(s.to) ? t('navUnpin') : t('navPin')} onClick={() => togglePin(s.to)}>
        {pins.includes(s.to) ? '★' : '☆'}
      </button>
    </div>
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="app">
      <aside className="sidebar" aria-label={t('appName')}>
        <Link to="/" className="brand" aria-label={t('appName')}>
          <OakMark />
          <Wordmark />
        </Link>
        <DecoRule />
        <ClaudeButton place="side" />
        <nav className="sidenav">
          {pinned.length ? (
            <div className="nav-section nav-pinned" data-testid="nav-pinned">
              <div className="nav-group">{t('navPinned')}</div>
              {pinned.map((s) => link(s, 'pin:'))}
            </div>
          ) : null}
          {groups.map((g) => {
            // a group opens with a tap and stays as left; the one holding the page on screen (one of its links, or a page
            // under it such as an invoice opened from Invoices) is always open, its name a plain heading. A link wins
            // over a group's addresses: Ledger is Finance's link though it sits under Administration's /settings.
            const here = g.items.some(isActive) || (!linkHere && (g.owns ?? []).some((o) => under(path, o)));
            const open = g.label === null || here || openKeys.includes(g.key);
            return (
              <div key={g.key} className="nav-section">
                {g.label && here ? (
                  <div className="nav-group nav-here">
                    <span>{g.label}</span>
                    <span className="nav-chev" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                ) : g.label ? (
                  <button type="button" className="nav-group nav-toggle" aria-expanded={open} onClick={() => toggleGroup(g.key)}>
                    <span>{g.label}</span>
                    <span className="nav-chev" aria-hidden="true">
                      {open ? '▾' : '▸'}
                    </span>
                  </button>
                ) : null}
                {open ? g.items.map((s) => link(s, '')) : null}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <SitePicker />
          <LocaleToggle />
          <button type="button" className="sidelink sign-out" onClick={() => void signOut()}>
            {t('signOut')}
          </button>
        </div>
      </aside>
      <div className="frame">
        <div className="top">
          <TopBar />
          <OfflineBanner />
        </div>
        <main className="content">
          <DeliverySync />
          <Outlet />
        </main>
        <nav className="tabbar">
          {sections.map((s) => (
            <NavLink key={s.to} to={s.to} end={s.end} className="tab">
              {({ isActive }) => (
                <>
                  <Diamond filled={isActive} />
                  <span>{s.label}</span>
                </>
              )}
            </NavLink>
          ))}
          <ClaudeButton place="tab" />
        </nav>
      </div>
    </div>
  );
}
